// EntityTimeline — the entity-scoped event timeline (FRONTEND-PATTERNS §Entity
// surfaces §History & record-changes). ONE grammar for the two trails an entity
// tells about itself:
//   • its LIFECYCLE story (the domain ledger — asset_events), an Overview
//     section, never a tab: most entities live 1–3 events and a tab is an empty
//     room.
//   • its RECORD CHANGES (the audit trail), behind a quiet door, in a SlideOver.
//   • what PEOPLE SAID about it — a file's notes (the author as the moment
//     word, the body as the line, "edited" as the foot, the ⋯ door in the slot).
// Same rail, same anatomy — so the trails read as one story told at three grains.
//
// Not to be confused with `Timeline` (the day-grouped, cursor-paged activity
// FEED — the platform audit page's own surface). This one is compact, bounded,
// and lives INSIDE a section: a rail with nodes (newest primary-tinted), and per
// entry a moment word + date lead → one quiet labelled from→to line per CHANGED
// axis → one human foot line. A born entry shows plain facts, no "— →".
//
// Data-driven on purpose: the caller maps its domain rows to `TimelineEntryData`
// and the component owns position (newest / last), the fold, and the rail
// geometry — so two consumers can never drift on where a node sits.
//
// i18n-free (the EmptyState/Badge contract): every word arrives as a prop.

import { useState, type ReactNode } from 'react';
import { Button } from '../ui/Button.js';

export interface TimelineEntryData {
  /** Stable key. */
  id: string;
  /** The moment word — its door's noun (AR uses the masdar; tense lives in the date). */
  title: string;
  /** The date (lifecycle: the effective day · audit: the datetime), already formatted. */
  when: string;
  /** One `TimelineAxis` per CHANGED axis / field. */
  lines?: ReactNode;
  /** The human foot — reason · "note" · actor, composed with `TimelineFoot`. */
  foot?: ReactNode;
  /** The per-entry door. An append-only ledger allows exactly one act (correct
   *  the date); an audit trail allows none, so it simply omits this. */
  door?: ReactNode;
}

/** One labelled line inside an entry — an axis (Location · Status · Condition)
 *  or an audit field. The label column is fixed so the values stack cleanly. */
export function TimelineAxis({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
      <span className="w-20 shrink-0 text-xs text-[var(--color-text-secondary)]">{label}</span>
      <span className="min-w-0">{children}</span>
    </div>
  );
}

/** A from→to pair. `from == null` renders the value alone (a born entry states
 *  plain facts — never "— → x"). The arrow follows reading direction. */
export function TimelinePair({ from, to }: { from?: ReactNode; to: ReactNode }) {
  if (from == null) return <>{to}</>;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1.5">
      {from}
      <span aria-hidden className="inline-block text-[var(--color-text-secondary)] rtl:-scale-x-100">
        →
      </span>
      {to}
    </span>
  );
}

/** The one human foot line — parts joined by an em dash (reason — "note" · actor). */
export function TimelineFoot({ parts }: { parts: ReactNode[] }) {
  const shown = parts.filter(Boolean);
  if (shown.length === 0) return null;
  return (
    <p className="text-xs text-[var(--color-text-secondary)]">
      {shown.map((part, i) => (
        <span key={i}>
          {i > 0 && <span aria-hidden>{' — '}</span>}
          {part}
        </span>
      ))}
    </p>
  );
}

function Entry({ entry, isNewest, isLast }: { entry: TimelineEntryData; isNewest: boolean; isLast: boolean }) {
  return (
    <li className="grid grid-cols-[auto_1fr] gap-x-3">
      {/* The rail. The node sits in a one-line-high box so it optically centers
          on the head row's first line; the connector fills the rest. */}
      <div className="flex flex-col items-center">
        <span aria-hidden className="flex h-5 items-center">
          <span
            className={`h-2 w-2 rounded-full ${
              isNewest
                ? 'bg-[var(--color-primary-display)]'
                : 'border border-[var(--color-border-strong)] bg-[var(--color-bg-surface)]'
            }`}
          />
        </span>
        {!isLast && <span aria-hidden className="w-px flex-1 bg-[var(--color-divider)]" />}
      </div>
      {/* The door is absolutely placed at the top-end so it never inflates the
          text flow (and the content reserves its width). */}
      <div className={`relative min-w-0 ${entry.door ? 'pe-9' : ''} ${isLast ? '' : 'pb-4'}`}>
        {entry.door && <span className="absolute end-0 top-0">{entry.door}</span>}
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-sm font-semibold text-[var(--color-text-primary)]">{entry.title}</span>
          <span className="text-xs text-[var(--color-text-secondary)]">{entry.when}</span>
        </div>
        {(entry.lines || entry.foot) && (
          <div className="mt-0.5 flex flex-col gap-0.5">
            {entry.lines}
            {entry.foot}
          </div>
        )}
      </div>
    </li>
  );
}

export interface EntityTimelineProps {
  /** Newest first — the caller orders (its ledger knows the tiebreak). */
  entries: TimelineEntryData[];
  /** Collapse past this many entries; omit for no fold. */
  foldAfter?: number;
  /** The fold's label, e.g. (n) => `Show all ${n}`. Required when `foldAfter` is set. */
  showAllLabel?: (total: number) => string;
  /** Rendered when there are no entries (a factual line, never a shrug). */
  empty?: ReactNode;
}

export function EntityTimeline({ entries, foldAfter, showAllLabel, empty }: EntityTimelineProps) {
  const [expanded, setExpanded] = useState(false);
  if (entries.length === 0) return <>{empty ?? null}</>;

  const folded = !expanded && foldAfter !== undefined && entries.length > foldAfter;
  const visible = folded ? entries.slice(0, foldAfter) : entries;

  return (
    <>
      <ol className="flex flex-col">
        {visible.map((entry, i) => (
          <Entry
            key={entry.id}
            entry={entry}
            isNewest={i === 0}
            isLast={!folded && i === visible.length - 1}
          />
        ))}
      </ol>
      {folded && showAllLabel && (
        <div className="ps-5">
          <Button variant="link" onClick={() => setExpanded(true)}>
            {showAllLabel(entries.length)}
          </Button>
        </div>
      )}
    </>
  );
}
